import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserRound, Check, Trash2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMembers({ token }) {
  const [members, setMembers] = useState(null);

  const headers = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(r.data);
    } catch {
      setMembers([]);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (m) => {
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(m.email)}/approve`, {}, headers());
      toast.success("Mitglied freigeschaltet", { description: `${m.name} kann sich jetzt im Forum anmelden.` });
      await load();
    } catch {
      toast.error("Freischalten fehlgeschlagen");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Konto von ${m.name} (${m.email}) wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(m.email)}`, headers());
      toast.success("Konto gelöscht");
      await load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <section className="mt-12" data-testid="admin-members">
      <div className="flex items-center gap-3 mb-5">
        <UserRound size={20} className="text-[#1A382B]" />
        <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">Forum-Mitglieder</h2>
      </div>
      {members === null ? (
        <p className="flex items-center gap-2 text-sm text-[#6F7E73]"><Loader2 size={15} className="animate-spin" /> Mitglieder werden geladen …</p>
      ) : members.length === 0 ? (
        <p className="rounded-2xl border border-[#D8D3C5] bg-white p-6 text-sm text-[#47544A]" data-testid="members-empty">
          Noch keine Registrierungen eingegangen.
        </p>
      ) : (
        <div className="space-y-3" data-testid="members-list">
          {members.map((m, i) => (
            <div key={m.email} className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#D8D3C5] bg-white p-5" data-testid={`member-${i}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B] font-display font-bold">
                {m.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#1A382B]">{m.name}{m.parcel ? <span className="font-normal text-[#6F7E73]"> · Parzelle {m.parcel}</span> : null}</p>
                <p className="text-sm text-[#47544A]">{m.email}</p>
              </div>
              {m.approved ? (
                <span className="rounded-full bg-[#E2ECE5] px-3 py-1 font-jbm text-xs tracking-wider uppercase text-[#1A382B]" data-testid={`member-status-${i}`}>
                  Freigeschaltet
                </span>
              ) : (
                <span className="rounded-full bg-[#D4A359]/20 px-3 py-1 font-jbm text-xs tracking-wider uppercase text-[#8a6420]" data-testid={`member-status-${i}`}>
                  Wartet auf Freigabe
                </span>
              )}
              <div className="flex shrink-0 gap-2">
                {!m.approved && (
                  <button onClick={() => approve(m)} data-testid={`member-approve-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A382B] px-4 py-2 text-xs font-semibold text-[#F8F7F2] transition-colors hover:bg-[#C85A32]">
                    <Check size={13} /> Freischalten
                  </button>
                )}
                <button onClick={() => remove(m)} data-testid={`member-delete-${i}`} aria-label="Konto löschen" className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#B93829] hover:border-[#B93829] transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
